import { useRef, useState, type ChangeEvent } from "react";
import { Icon } from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { useConfirm } from "@/hooks/useConfirm";
import { useMyDevices } from "@/hooks/useMyDevices";
import type { MyProfileState } from "@/hooks/useMyProfile";
import { makeProfilePhoto } from "@/lib/profilePhoto";
import { removeMyProfilePhoto, saveMyProfilePhoto } from "@/services/my_account.repository";
import { BIZ_META, SPACE_META, defaultBiz } from "@/domain/space";
import { useAllUnitRoles } from "@/hooks/useRoles";
import { Avatar } from "./Avatar";
import { PasswordChangeModal } from "./PasswordChangeModal";

/**
 * ProfileTab — D-1 회원정보 관리(12 설계 §8-D).
 *  - 프로필 이미지: 올리면 자동으로 정사각 자르기·128px·회전 반영·용량 맞춤 → preferences/profile
 *  - 이름·소속·직위: 읽기 전용 + "관리자에게 요청"
 *  - 휴대폰: 보기 + [변경] 꺼짐("추후 제공")
 *  - 비밀번호: 평면 폼 모달
 */
export function ProfileTab({ profile }: { profile: MyProfileState }) {
  const { user, sharedSession } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const { devices, currentId } = useMyDevices();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const { labelOf } = useAllUnitRoles();

  if (!user) return null;
  const { photo, account, memberships } = profile;
  const otherDevices = devices.filter((d) => d.id !== currentId).length;

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // 같은 파일을 다시 골라도 change 가 오게
    if (!file || !user) return;
    setBusy(true);
    try {
      const url = await makeProfilePhoto(file);
      await saveMyProfilePhoto(user.uid, url);
      toast.success("프로필 사진을 바꿨습니다.");
    } catch (err) {
      const code = (err as { code?: string }).code;
      toast.error(
        code === "permission-denied"
          ? "사진을 저장할 권한이 없습니다. 서버 준비가 끝나면 다시 시도하세요."
          : (err as Error).message || "사진을 저장하지 못했습니다.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    if (!user) return;
    const ok = await confirm({ title: "프로필 사진을 지울까요?", summary: "이름 첫 글자로 표시됩니다.", confirmLabel: "지우기", kind: "warning" });
    if (!ok) return;
    setBusy(true);
    try {
      await removeMyProfilePhoto(user.uid);
      toast.success("프로필 사진을 지웠습니다.");
    } catch (err) {
      toast.error(`지우지 못했습니다: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const affRows =
    memberships.length > 0
      ? memberships.flatMap((m) =>
          // 소속 = 법인 · 사업부 · 그 사업부 부서 — (법인, 사업부)마다 한 줄(2026-10-26 사업부별 부서)
          (m.units.length ? m.units : [{ biz: null, role: m.role }]).map((u) => ({
            key: `${m.tenant}:${u.biz ?? "-"}`,
            org: [SPACE_META[m.tenant].label, u.biz ? BIZ_META[u.biz].label : "", u.role ? labelOf(m.tenant, u.biz ?? defaultBiz(m.tenant), u.role) : ""]
              .filter(Boolean).join(" · "),
          })),
        )
      : [
          {
            key: "none",
            org: (account?.tenants?.length ? account.tenants : user.tenants ?? []).map((t) => SPACE_META[t].label).join(" · ") ||
              (user.isAdmin ? "전체 관리자" : ""),
          },
        ];
  // 통합 직급(users.rank — 사람당 1개). 계정 문서가 아직 없으면 소속 사본 값
  const rank = account?.rank || memberships.find((m) => m.rank)?.rank || "";

  return (
    <div className="ma-pane">
      <section className="ma-sec">
        <h3 className="ma-sec-title">프로필 이미지</h3>
        <div className="ma-photo-row">
          <Avatar className="ma-av is-lg" photo={photo} name={user.name} loginId={user.loginId} />
          <div className="ma-photo-ctl">
            <div className="ma-btn-row">
              <button type="button" className="btn-ghost" onClick={() => fileRef.current?.click()} disabled={busy}>
                <Icon name="image" size={16} />
                {busy ? "처리 중…" : "이미지 변경"}
              </button>
              {photo && (
                <button type="button" className="btn-ghost is-danger" onClick={() => void onRemove()} disabled={busy}>
                  사진 지우기
                </button>
              )}
            </div>
            <p className="ma-help">사진을 고르면 가운데를 정사각형으로 잘라 작게 저장합니다. 사진이 없으면 이름 첫 글자로 보입니다.</p>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => void onPick(e)} />
          </div>
        </div>
      </section>

      <section className="ma-sec">
        <div className="ma-sec-hd">
          <h3 className="ma-sec-title">기본 정보</h3>
          <span className="ma-chip-note">이름·소속·직급을 바꾸려면 관리자에게 요청하세요</span>
        </div>
        <div className="ma-ro-grid">
          <div className="ma-ro-pair">
            <div className="ma-ro">
              <span className="ma-ro-lbl">이름</span>
              <span className="ma-ro-val">{user.name || "—"}</span>
            </div>
            <div className="ma-ro">
              <span className="ma-ro-lbl">직급</span>
              <span className="ma-ro-val">{rank || "—"}</span>
            </div>
          </div>
          {affRows.map((r) => (
            <div className="ma-ro" key={r.key}>
              <span className="ma-ro-lbl">소속</span>
              <span className="ma-ro-val">{r.org || "—"}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="ma-sec">
        <h3 className="ma-sec-title">연락처</h3>
        <div className="ma-inline">
          <span className="ma-ro-box">{account?.phone || "등록된 휴대폰 번호가 없습니다"}</span>
          <button type="button" className="btn-ghost" disabled>
            변경
          </button>
          <span className="ma-help">추후 제공 — 지금은 관리자에게 요청하세요</span>
        </div>
      </section>

      <section className="ma-sec">
        <h3 className="ma-sec-title">이메일</h3>
        <div className="ma-inline">
          <span className="ma-ro-box">{account?.email || "등록된 이메일이 없습니다"}</span>
          <button type="button" className="btn-ghost" disabled>
            변경
          </button>
          <span className="ma-help">추후 제공 — 지금은 관리자에게 요청하세요</span>
        </div>
      </section>

      <section className="ma-sec">
        <h3 className="ma-sec-title">비밀번호</h3>
        <div className="ma-inline">
          <button type="button" className="btn-ghost" onClick={() => setPwOpen(true)}>
            <Icon name="shield" size={16} />
            비밀번호 변경
          </button>
        </div>
        <p className="ma-help">
          아이디 <b className="mono">{user.loginId || "—"}</b> · 아이디는 바꿀 수 없습니다
          {sharedSession && " · 지금은 공용 PC로 로그인했습니다"}
        </p>
      </section>

      {pwOpen && <PasswordChangeModal otherDevices={otherDevices} onClose={() => setPwOpen(false)} />}
    </div>
  );
}
